import type { Media, Persisted } from '@tunasong/schemas'
import { playbackUrlOf } from '@tunasong/schemas'
import { create } from 'zustand'

export interface PlaylistTrack {
  media: Pick<Persisted<Media>, 'id' | 'name' | 'storageUrls'>

  /** The source of the media - e.g., a playlist - to be used for a backlink */
  sourceEntityId?: string
}

/**
 * Play and pause through the one global player's controller. Registered by GlobalAudioPlayer, so a play button
 * can start audio without running a controller of its own: each `useGlobalPlayer` re-renders on every
 * `timeupdate` of the shared element, which a list of audio links cannot afford.
 */
export interface GlobalPlayerCommands {
  playNow(media: Persisted<Media>[], sourceEntityId?: string): void
  pause(): void
}

/** Global state. We want to render `formDialog` on top (app) level */
export const useGlobalPlayerState = create<{
  playlist: PlaylistTrack[]
  currentSrc?: string
  active?: PlaylistTrack
  /** Whether the shared element is playing, from its own play/pause events. Select it to follow playback cheaply. */
  playing: boolean
  commands: GlobalPlayerCommands | null
  setPlayList: (playlist: PlaylistTrack[], active?: PlaylistTrack) => void
  setActive: (media: PlaylistTrack) => void
  setPlaying: (playing: boolean) => void
  setCommands: (commands: GlobalPlayerCommands | null) => void
  playerRef: HTMLAudioElement | null
  setPlayerRef: (playerRef: HTMLAudioElement | null) => void
  /**
   * Sources (e.g. a playlist) that show a failed track's error inline, next to the track. While one is mounted,
   * the global player does not also raise an alert for its tracks: one message per failure (GH #2008).
   */
  inlineErrorSources: string[]
  addInlineErrorSource: (sourceEntityId: string) => void
  removeInlineErrorSource: (sourceEntityId: string) => void
}>(set => ({
  playlist: [],
  playing: false,
  commands: null,
  playerRef: null,
  setPlaying: (playing: boolean) => set({ playing }),
  setCommands: (commands: GlobalPlayerCommands | null) => set({ commands }),
  setActive: (track: PlaylistTrack) => set({ active: track, currentSrc: playbackUrlOf(track.media?.storageUrls) }),
  setPlayerRef: (playerRef: HTMLAudioElement | null) => set({ playerRef }),
  setPlayList: (playlist: PlaylistTrack[]) => set({ playlist }),
  inlineErrorSources: [],
  addInlineErrorSource: (sourceEntityId: string) =>
    set(state => ({ inlineErrorSources: [...state.inlineErrorSources, sourceEntityId] })),
  /** Removes one registration, so two mounts of the same playlist each keep their own */
  removeInlineErrorSource: (sourceEntityId: string) =>
    set(state => {
      const index = state.inlineErrorSources.indexOf(sourceEntityId)
      return index < 0
        ? state
        : { inlineErrorSources: state.inlineErrorSources.filter((_source, idx) => idx !== index) }
    }),
}))
